import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  Grid,
  useTheme,
  IconButton,
  Chip,
  Box,
  CircularProgress
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useLazyGetMaintenanceByIdQuery } from "../features/maintenance/MaintenanceSlice";
import { t } from 'i18next';
import { logOut } from '../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';

export interface MaintenanceRecordDetail {
  id: number;
  stationId: number;
  staff: string;
  title: string;
  description: string;
  maintenanceDate: Date;
  maintenanceDateTo: Date;
  attachments: attachmentsProps[];
}

interface attachmentsProps {
  id: number;
  contentType: string;
  url: string;
}

interface DetailDialogProps {
  open: boolean;
  onClose: () => void;
  maintenanceId: number;
}

const MaintenanceRecordDetailDialog = ({ open, onClose, maintenanceId }: DetailDialogProps) => {

  const theme = useTheme();
  const [getMaintenanceDetail] = useLazyGetMaintenanceByIdQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<MaintenanceRecordDetail | null>();
  //if (!record) return null;

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClosePhoto = () => {
    setIsImageOpen(false);
    setSelectedImage(null);
  };

  const handlePhotoClick = (imageUrl: string) => {
    // console.log("Button clicked for row:", rowData);
    // Add your custom logic here
    setSelectedImage(imageUrl);
    setIsImageOpen(true);
  };

  useEffect(() => {
    try {
      setIsLoading(true);
      getMaintenanceDetail(maintenanceId.toString()).then(
        (res) => {
          if (res.status === 'fulfilled') {
            setData(res.data);
            setIsLoading(false);
          }

          if (res.status === 'rejected') {
            alert(t('An error occurred. Please login again.'));
            dispatch(logOut());
            navigate('/');
          }

        }
      )
    } catch (error) {

    }
  }, [maintenanceId]);// eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: '80vh',
        }
      }}
    >
      <Dialog
        open={isImageOpen}
        onClose={handleClosePhoto}
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ padding: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={handleClosePhoto}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
              }}
            >
            </IconButton>
            <img
              src={selectedImage || ''}
              alt="Full preview"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                borderRadius: 8
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
          { isLoading ? 'Loading...' :  data.title }
            </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: 'auto' }}>
      { isLoading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 300
          }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
        <Grid container spacing={4}>
          {/* Details Section */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('ID')}
                </Typography>
                <Chip label={data.id} variant="outlined" />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('Station ID')}
                </Typography>
                <Chip label={data.stationId} variant="outlined" />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('Staff')}
                </Typography>
                <Typography variant="body1">
                  {data.staff ? data.staff : t('Unknown')}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('Maintenance From Date')}
                </Typography>
                <Typography variant="body1">
                  {format(new Date(data.maintenanceDate), 'PPpp')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('Maintenance To Date')}
                </Typography>
                <Typography variant="body1">
                  {format(new Date(data.maintenanceDateTo), 'PPpp')}
                </Typography>
              </Grid>


              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('Description')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    backgroundColor: theme.palette.grey[50],
                    p: 2,
                    borderRadius: 2
                  }}
                >
                  {data.description}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Attachments Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              {t('Attachments')} ({data.attachments?.length || 0})
            </Typography>

            <Box sx={{
              maxHeight: 400,
              overflowY: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2
            }}>
              <Grid container spacing={2}>
                {data.attachments?.map((attachment, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <Avatar
                      variant="rounded"
                      src={`${attachment.url}?token=${localStorage.getItem('accessToken')}`}
                      sx={{
                        width: '100%',
                        height: 100,
                        bgcolor: theme.palette.grey[200],
                        '& img': {
                          objectFit: 'cover'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePhotoClick(`${attachment.url}?token=${localStorage.getItem('accessToken')}`)}
                      }
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder-image.jpg';
                      }}
                    >
                      <Typography variant="caption">{t('Attachments')} {index + 1}</Typography>
                    </Avatar>
                  </Grid>
                ))}
                {(!data.attachments || data.attachments.length === 0) && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {t('No attachments available')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
         )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          startIcon={<Close />}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceRecordDetailDialog;